import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { paths } from '../constants';

interface LayoutContextType {
  isButtonVisible: boolean;
  setButtonVisible: Dispatch<SetStateAction<boolean>>;
  hasButtonBeenFocused: boolean;
  setHasButtonBeenFocused: Dispatch<SetStateAction<boolean>>;
}

export const LayoutContext = React.createContext<LayoutContextType>(
  {} as LayoutContextType
);

export const LayoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isButtonVisible, setButtonVisible] = useState(false);
  const [previousPath, setPreviousPath] = useState('');
  const [hasButtonBeenFocused, setHasButtonBeenFocused] = useState(false);

  const location = useLocation();

  const shouldResetGoToContentButtonState = useMemo(
    () =>
      location.pathname !== previousPath &&
      !(location.pathname === paths.trips.root && previousPath === paths.root),
    [location.pathname, previousPath]
  );
  useEffect(() => {
    const rootPaths: string[] = [
      paths.trips.root,
      paths.passengers.root,
      paths.users.root,
    ];

    if (shouldResetGoToContentButtonState) {
      setPreviousPath(location.pathname);
      setButtonVisible(false);
      if (rootPaths.includes(location.pathname)) {
        setHasButtonBeenFocused(false);
      }
    }
  }, [
    location,
    setPreviousPath,
    previousPath,
    setButtonVisible,
    setHasButtonBeenFocused,
    shouldResetGoToContentButtonState,
  ]);

  const value = useMemo(
    () => ({
      isButtonVisible,
      setButtonVisible,
      hasButtonBeenFocused,
      setHasButtonBeenFocused,
    }),
    [isButtonVisible, hasButtonBeenFocused]
  );

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => React.useContext(LayoutContext);
